import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore } from "@/stores";
import type IItemBlock from "@/models/responses/IItemBlock";
import UpsertItemBlockRequest from "@/models/requests/UpsertItemBlockRequest";

export const useItemBlocksStore = defineStore("itemblocks", {
  state: () => ({
    error: "" as any,
    data: [] as IItemBlock[],
    loadedSiteId: 0,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedSiteId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedSiteId === selectedSiteId.value) return;

      this.error = "";
      await api
        .get<IItemBlock[]>(`v1.0/item-blocks?siteId=${selectedSiteId.value}`)
        .then((response) => {
          this.data = response.data;
          this.loadedSiteId = selectedSiteId.value;
         })
        .catch((error) => this.error = error);
    },
    async fetch(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .get<IItemBlock>(`v1.0/item-blocks/${id}`)
        .then((response) => {
          const index = this.data.findIndex((x) => x.id === id);
          if (index === -1) this.data.push(response.data);
          else this.data[index] = response.data;
        })
        .catch((error) => (this.error = error));
    },
    async upsert(itemBlock: UpsertItemBlockRequest, onfulfilled: (data: IItemBlock) => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<IItemBlock>(`v1.0/item-blocks`, itemBlock)
        .then((response) => {
          const index = this.data.findIndex((x) => x.id === response.data.id);
          if (index === -1) this.data.push(response.data);
          else this.data[index] = response.data;
          onfulfilled(response.data);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async delete(itemBlockId: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .delete(`v1.0/item-blocks/${itemBlockId}`)
        .then(() => {
          this.data = this.data.filter((x) => x.id !== itemBlockId);
        })
        .catch((error) => (this.error = error));
    },
  },
  getters: {
    getById: (state) => (id: number): IItemBlock => {
      return <IItemBlock>(
        (state.data as IItemBlock[]).find((x) => x.id === id)
      );
    },
    getByCode: (state) => (code: string): IItemBlock => {
      return <IItemBlock>(
        (state.data as IItemBlock[]).find((x) => x.code === code)
      );
    },
  },
});
